import { hasCommunityListScopeType, hasGroupScopeType, isLimitedAdminScope } from '@/helpers/adminScope';
import { SCOPE_TYPE } from '@/types';

const GROUP_SCOPE_ALLOWED_PATH_PATTERNS = [
  /^\/admin\/church\/[^/]+\/dashboard(?:\/|$)/,
  /^\/admin\/church\/[^/]+\/groups(?:\/|$)/,
  /^\/admin\/church\/[^/]+\/calendars\/lookup(?:\/|$)/,
  /^\/admin\/church\/[^/]+\/calendar-event-locations\/lookup(?:\/|$)/,
  /^\/admin\/church\/[^/]+\/members(?:\/|$)/,
  /^\/admin\/church\/[^/]+\/messages(?:\/|$)/,
  /^\/admin\/church\/[^/]+\/message-templates(?:\/|$)/,
  /^\/admin\/church\/[^/]+\/forms(?:\/|$)/,
  /^\/admin\/church\/[^/]+\/form-templates(?:\/|$)/,
  /^\/admin\/church\/[^/]+\/church-context(?:\/|$)/,
  // Dual Group Admin + List Admin still uses group scope, but Community is a valid section for that user.
  /^\/admin\/church\/[^/]+\/community(?:\/|$)/,
  /^\/admin\/churches\/[^/]+\/products(?:\/|$)/,
  /^\/admin\/churches\/[^/]+\/list(?:\/|$)/,
];

const COMMUNITY_LIST_SCOPE_ALLOWED_PATH_PATTERNS = [
  /^\/admin\/church\/[^/]+\/community(?:\/|$)/,
  // List Admins can send messages from Community and should reach Messages/Forms products.
  /^\/admin\/church\/[^/]+\/messages(?:\/|$)/,
  /^\/admin\/church\/[^/]+\/announcement-messages(?:\/|$)/,
  /^\/admin\/church\/[^/]+\/message-templates(?:\/|$)/,
  /^\/admin\/church\/[^/]+\/forms(?:\/|$)/,
  /^\/admin\/church\/[^/]+\/form-templates(?:\/|$)/,
  /^\/admin\/church\/[^/]+\/church-context(?:\/|$)/,
  /^\/admin\/churches\/[^/]+\/products(?:\/|$)/,
];

type ChurchApiScopeParams = {
  isGroupScope: boolean | undefined;
  isLimitedScope?: boolean;
  scopeTypes?: SCOPE_TYPE[];
  requestPathname: string;
};

export const isChurchApiAllowedForScope = ({
  isGroupScope,
  isLimitedScope,
  scopeTypes,
  requestPathname,
}: ChurchApiScopeParams): boolean => {
  if (!isLimitedAdminScope({ isLimitedScope, isGroupScope })) {
    return true;
  }

  if (!requestPathname.startsWith('/admin/church/') && !requestPathname.startsWith('/admin/churches/')) {
    return true;
  }

  const hasGroup = hasGroupScopeType({ scopeTypes, isGroupScope });
  const hasCommunityList = hasCommunityListScopeType({ scopeTypes });

  if (hasGroup) {
    return GROUP_SCOPE_ALLOWED_PATH_PATTERNS.some((pattern) => pattern.test(requestPathname));
  }

  if (hasCommunityList) {
    return COMMUNITY_LIST_SCOPE_ALLOWED_PATH_PATTERNS.some((pattern) => pattern.test(requestPathname));
  }

  // Limited scope without recognizable scopeTypes: keep legacy group allowlist via isGroupScope.
  if (isGroupScope === true) {
    return GROUP_SCOPE_ALLOWED_PATH_PATTERNS.some((pattern) => pattern.test(requestPathname));
  }

  return false;
};
